/**
 * Admin Frameworks service, backed by Firestore.
 * A responsibility's default frameworks live as a `frameworks` array
 * ([{ promptId, isGlobalPrompt }]) on the responsibility doc itself.
 */

import { collection, doc, getDoc, getDocs, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../../../shared/firebase';

const now = () => new Date().toISOString();

async function getFrameworksArray(responsibilityId) {
  const snap = await getDoc(doc(db, 'responsibilities', responsibilityId));
  if (!snap.exists()) {
    throw new Error('Responsibility not found');
  }
  const data = snap.data();
  return Array.isArray(data.frameworks) ? data.frameworks : [];
}

async function promptSummary(promptId, isGlobalPrompt) {
  const ref = isGlobalPrompt ? doc(db, 'globalPrompts', promptId) : doc(db, 'prompts', promptId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return { promptTitle: null, promptDescription: null };
  const data = snap.data();
  return {
    promptTitle: data.title || null,
    promptDescription: data.description || null,
    orgDomain: isGlobalPrompt ? undefined : data.orgId,
  };
}

/**
 * Get prompts assigned to a responsibility
 * @returns {Promise<Array>}
 */
export const getFrameworksByResponsibilityId = async (responsibilityId) => {
  try {
    const frameworks = await getFrameworksArray(responsibilityId);
    return Promise.all(
      frameworks.map(async (fw) => ({
        responsibilityId,
        promptId: fw.promptId,
        isGlobalPrompt: fw.isGlobalPrompt === true,
        ...(await promptSummary(fw.promptId, fw.isGlobalPrompt === true)),
      }))
    );
  } catch (error) {
    console.error('Error fetching frameworks:', error);
    throw error;
  }
};

/**
 * Assign prompt(s) to responsibility (appends without duplicating)
 * @returns {Promise<Object>}
 */
export const assignFrameworks = async (responsibilityId, promptIds, isGlobalPrompts) => {
  try {
    const existing = await getFrameworksArray(responsibilityId);
    const seen = new Set(existing.map((f) => `${f.promptId}:${f.isGlobalPrompt === true}`));
    const merged = [...existing];
    promptIds.forEach((promptId, i) => {
      const isGlobal = isGlobalPrompts[i] === true;
      if (!seen.has(`${promptId}:${isGlobal}`)) {
        merged.push({ promptId, isGlobalPrompt: isGlobal });
        seen.add(`${promptId}:${isGlobal}`);
      }
    });
    await updateDoc(doc(db, 'responsibilities', responsibilityId), {
      frameworks: merged,
      updatedAt: now(),
    });
    return { success: true, count: merged.length };
  } catch (error) {
    console.error('Error assigning frameworks:', error);
    throw error;
  }
};

/**
 * Remove prompt from responsibility
 * @returns {Promise<Object>}
 */
export const removeFramework = async (responsibilityId, promptId, isGlobalPrompt) => {
  try {
    const existing = await getFrameworksArray(responsibilityId);
    const filtered = existing.filter(
      (f) => !(f.promptId === promptId && (f.isGlobalPrompt === true) === (isGlobalPrompt === true))
    );
    await updateDoc(doc(db, 'responsibilities', responsibilityId), {
      frameworks: filtered,
      updatedAt: now(),
    });
    return { success: true, message: 'Framework removed successfully' };
  } catch (error) {
    console.error('Error removing framework:', error);
    throw error;
  }
};

/**
 * Get all global prompts (for selection)
 * @returns {Promise<Array>}
 */
export const getAllGlobalPrompts = async () => {
  try {
    const snap = await getDocs(collection(db, 'globalPrompts'));
    const prompts = [];
    snap.forEach((d) => {
      const data = d.data();
      prompts.push({
        id: data.promptId,
        promptId: data.promptId,
        title: data.title,
        description: data.description,
        isGlobal: true,
      });
    });
    return prompts;
  } catch (error) {
    console.error('Error fetching global prompts:', error);
    throw error;
  }
};

/**
 * Get available prompts for a role (global + org-specific if role has orgId)
 * @returns {Promise<Array>} Array of prompts with isGlobal flag
 */
export const getAvailablePromptsForRole = async (roleId) => {
  try {
    const roleSnap = await getDoc(doc(db, 'roles', roleId));
    if (!roleSnap.exists()) {
      throw new Error('Role not found');
    }
    const role = roleSnap.data();

    const allPrompts = await getAllGlobalPrompts();

    if (role.orgId) {
      const orgSnap = await getDocs(query(collection(db, 'prompts'), where('orgId', '==', role.orgId)));
      orgSnap.forEach((d) => {
        const data = d.data();
        allPrompts.push({
          id: data.promptId,
          promptId: data.promptId,
          title: data.title,
          description: data.description,
          isGlobal: false,
          orgDomain: role.orgId,
        });
      });
    }

    return allPrompts;
  } catch (error) {
    console.error('Error fetching prompts for role:', error);
    throw error;
  }
};
